/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";

import type { ThemeType } from "../../../../../../global/emotion";
import type { CarouselApi } from "../../../../../hooks/useCarousel";
import { useThemeValues } from "../../../../../hooks/useThemeValues";
import { cssMediaQueryMinWidthTablet } from "../../../../../lib/constants/emotionMediaQueries.constants";
import type { IconButtonProps } from "../../../../_base/_core/IconButton/IconButton.emotion";
import { IconButton } from "../../../../_base/_core/IconButton/IconButton.emotion";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "../../../../_base/icons/Icon/NavigationIcons";

const buttonStyle: Interpolation<ThemeType> = (theme) => ({
  background: theme.semanticColors.secondary,
  borderColor: theme.semanticColors.neutralsLowest,
  borderStyle: "solid",
  borderWidth: "2px",
  boxShadow: `0 0 16px 0 ${theme.semanticColors.higherContrastLowest}`,
  cursor: "pointer",
  display: "none",
  marginTop: "-20px",
  opacity: 0,
  position: "absolute",
  scale: 0.95,
  top: "50%",
  transition:
    "opacity 200ms ease-in, scale 150ms ease-in, display 200ms allow-discrete",
  zIndex: 10,
  ":hover": {
    backgroundColor: theme.semanticColors.secondary,
    scale: 1.05,
  },
});
const buttonHoverStyle: Interpolation = {
  "@starting-style": {
    opacity: 0,
    scale: 0.95,
  },
  display: "none",
  [cssMediaQueryMinWidthTablet]: { display: "flex" },
  opacity: 1,
  scale: 1,
};
const nextStyle: Interpolation<{ pageOffset: CarouselApi["pageOffset"] }> = (
  props,
) => ({
  right: `${props.pageOffset[1][0]}px`,
  [cssMediaQueryMinWidthTablet]: { right: `${props.pageOffset[1][1]}px` },
  transform: "translate(50%, 0)",
});
const previousStyle: Interpolation<{
  pageOffset: CarouselApi["pageOffset"];
}> = (props) => ({
  left: `${props.pageOffset[0][0]}px`,
  [cssMediaQueryMinWidthTablet]: { left: `${props.pageOffset[0][1]}px` },
  transform: "translate(-50%, 0)",
});

export type CarouselButtonProps = Partial<IconButtonProps> & {
  carouselApi: CarouselApi;
  direction: "next" | "previous";
  visible?: boolean;
};

export const CarouselButton = ({
  carouselApi,
  direction,
  visible = false,
  ...props
}: CarouselButtonProps) => {
  const theme = useThemeValues();
  return (
    <IconButton
      icon={
        direction === "next" ? (
          <ChevronRightIcon fill={theme.semanticColors.primary} />
        ) : (
          <ChevronLeftIcon fill={theme.semanticColors.primary} />
        )
      }
      size="m"
      css={[
        buttonStyle,
        direction === "next"
          ? nextStyle(carouselApi)
          : previousStyle(carouselApi),
        visible ? buttonHoverStyle : null,
      ]}
      {...props}
    />
  );
};
